// src/components/mpi/WorkItemPresetTable.js
import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Typography,
} from '@mui/material';
import DeleteWorkItemPresetButton from './DeleteWorkItemPresetButton';
import DetailPanel from './DetailPanel';
import { updateWorkItemPreset } from '../../actions/apiWorkItemPresets';

const WorkItemPresetTable = () => {
  const dispatch = useDispatch();
  const work_item_presets = useSelector(state => state.settings_refresh?.work_item_presets || []);
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);
  const all_departments = useSelector(state => state.settings_departments?.original_departments || []);
  const all_users = useSelector(state => state.settings_users?.users || []);

  const [rowSelection, setRowSelection] = useState({});
  const [massDealerships, setMassDealerships] = useState([]);
  const [massActive, setMassActive] = useState('');
  const [massPdfShow, setMassPdfShow] = useState('');
  const [massCategory, setMassCategory] = useState(''); // New state for category
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [tableKey, setTableKey] = useState(0);

  const dealershipOptions = useMemo(() => {
    return related_installations.map(inst => ({
      value: inst.subdomain,
      label: inst.softname || inst.subdomain,
    }));
  }, [related_installations]);

  const departmentOptions = useMemo(() => {
    return all_departments.map(dept => ({
      value: dept.id,
      label: dept.display_name || dept.name,
    }));
  }, [all_departments]);

  const userOptions = useMemo(() => {
    return all_users.map(user => ({
      value: user.id,
      label: user.user_name || user.email,
    }));
  }, [all_users]);

  const categoryOptions = ['cosmetic', 'mechanical', 'other'];

  // COLUMNS
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      size: 180,
      enableEditing: true,
      muiEditTextFieldProps: ({ row, table }) => ({
        onBlur: async (e) => {
          const value = e.target.value;
          const id = row.original.id;
          try {
            await dispatch(updateWorkItemPreset(id, { name: value }));
            setTableKey(prev => prev + 1);
            table.setEditingCell(null);
          } catch (err) {
            setError('Failed to update name');
          }
        },
      }),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 220,
      enableEditing: true,
      muiEditTextFieldProps: ({ row, table }) => ({
        multiline: true,
        rows: 2,
        onBlur: async (e) => {
          const value = e.target.value;
          const id = row.original.id;
          try {
            await dispatch(updateWorkItemPreset(id, { description: value }));
            setTableKey(prev => prev + 1);
            table.setEditingCell(null);
          } catch (err) {
            setError('Failed to update description');
          }
        },
      }),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      size: 140,
      enableEditing: true,
      editVariant: 'select',
      filterVariant: 'select',
      filterSelectOptions: categoryOptions.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) })),
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return row.getValue(id) === filterValue;
      },
      Filter: ({ column }) => (
        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Category"
          >
            <MenuItem value="">All</MenuItem>
            {categoryOptions.map(c => (
              <MenuItem key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      muiEditTextFieldProps: ({ row, table }) => ({
        select: true,
        value: row.original.category || '',
        onChange: async (e) => {
          const value = e.target.value;
          const id = row.original.id;
          try {
            await dispatch(updateWorkItemPreset(id, { category: value }));
            setTableKey(prev => prev + 1);
            table.setEditingCell(null);
          } catch (err) {
            setError('Failed to update category');
          }
        },
        children: categoryOptions.map(c => (
          <MenuItem key={c} value={c}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </MenuItem>
        )),
      }),
    },
    {
      accessorKey: 'active',
      header: 'Active',
      size: 80,
      enableEditing: true,
      editVariant: 'select',
      filterVariant: 'select',
      filterSelectOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id)) === filterValue;
      },
      Filter: ({ column }) => (
        <FormControl fullWidth>
          <InputLabel>Active</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Active"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
      ),
      muiEditTextFieldProps: ({ row, table }) => ({
        select: true,
        value: String(row.original.active),
        onChange: async (e) => {
          const value = e.target.value === 'true';
          const id = row.original.id;
          try {
            await dispatch(updateWorkItemPreset(id, { active: value }));
            setTableKey(prev => prev + 1);
            table.setEditingCell(null);
          } catch (err) {
            setError('Failed to update active');
          }
        },
        children: [
          <MenuItem key="true" value="true">Active</MenuItem>,
          <MenuItem key="false" value="false">Inactive</MenuItem>,
        ],
      }),
      Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'dvi_pdf_show',
      header: 'PDF Show',
      size: 100,
      enableEditing: true,
      editVariant: 'select',
      filterVariant: 'select',
      filterSelectOptions: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id)) === filterValue;
      },
      Filter: ({ column }) => (
        <FormControl fullWidth>
          <InputLabel>PDF Show</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="PDF Show"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      ),
      muiEditTextFieldProps: ({ row, table }) => ({
        select: true,
        value: String(row.original.dvi_pdf_show),
        onChange: async (e) => {
          const value = e.target.value === 'true';
          const id = row.original.id;
          try {
            await dispatch(updateWorkItemPreset(id, { dvi_pdf_show: value }));
            setTableKey(prev => prev + 1);
            table.setEditingCell(null);
          } catch (err) {
            setError('Failed to update PDF show');
          }
        },
        children: [
          <MenuItem key="true" value="true">Yes</MenuItem>,
          <MenuItem key="false" value="false">No</MenuItem>,
        ],
      }),
      Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'dealership',
      header: 'Dealership',
      size: 200,
      muiTableBodyCellProps: { 
        sx: { 
          padding: '8px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          '&:hover': { overflow: 'visible', whiteSpace: 'normal', backgroundColor: '#f5f5f5', zIndex: 1, position: 'relative', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }
        } 
      },
      enableEditing: true,
      editVariant: 'select',
      filterVariant: 'select',
      filterSelectOptions: dealershipOptions,
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        const value = row.getValue(id);
        if (!value) return false;
        return value.split(',').map(v => v.trim()).includes(filterValue);
      },
      Filter: ({ column }) => (
        <FormControl fullWidth>
          <InputLabel>Dealership</InputLabel>
          <Select
            value={column.getFilterValue() || ''}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            label="Dealership"
          >
            <MenuItem value="">All</MenuItem>
            {dealershipOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      muiEditTextFieldProps: ({ row, table }) => {
        const dealershipArray = row.original.dealership && typeof row.original.dealership === 'string'
          ? row.original.dealership.split(',').filter(Boolean)
          : [];
        return {
          select: true,
          multiple: true,
          value: dealershipArray,
          SelectProps: {
            renderValue: (selected) => (Array.isArray(selected) ? selected.join(', ') : ''),
            MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
            sx: { zIndex: 1000 },
            multiple: true,
          },
          children: dealershipOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={dealershipArray.includes(opt.value)} />
              {opt.label}
            </MenuItem>
          )),
          onChange: async (event) => {
            const selectedValues = Array.isArray(event.target.value)
              ? event.target.value
              : typeof event.target.value === 'string' && event.target.value
              ? event.target.value.split(',')
              : [];
            const id = row.original.id;
            const data = { dealership: selectedValues.join(',') };

            try {
              await dispatch(updateWorkItemPreset(id, data));
              setTableKey(prev => prev + 1);
              table.setEditingCell(null);
            } catch (ex) {
              setError('Failed to update dealership. Please try again.');
              console.error('Dealership update error:', ex);
            }
          },
          onClick: (event) => event.stopPropagation(),
        };
      },
      Cell: ({ row }) => {
        const dealerships = row.original.dealership && typeof row.original.dealership === 'string'
          ? row.original.dealership.split(',').filter(Boolean)
          : [];
        const displayNames = dealerships
          .map(subdomain => {
            const opt = dealershipOptions.find(o => o.value === subdomain);
            return opt ? opt.label : subdomain;
          })
          .join(', ');
        return displayNames || 'None';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DeleteWorkItemPresetButton work_item_preset_id={row.original.id} />
        </Box>
      ),
    },
  ], [dealershipOptions, dispatch]);

  const data = work_item_presets.map(p => ({
    ...p,
    active: p.active === true || p.active === 'true',
    dvi_pdf_show: p.dvi_pdf_show === true || p.dvi_pdf_show === 'true',
    dealership: p.dealership || '',
  }));

  // MASS ASSIGN
  const handleMassAssign = async () => {
    const selected = Object.keys(rowSelection).map(i => data[parseInt(i)]);
    if (selected.length === 0) return;

    setUpdating(true);
    setError(null);

    try {
      const promises = selected.map(p => {
        const updates = {};
        if (massDealerships.length > 0) updates.dealership = massDealerships.join(',');
        if (massActive !== '') updates.active = massActive === 'true';
        if (massPdfShow !== '') updates.dvi_pdf_show = massPdfShow === 'true';
        if (massCategory !== '') updates.category = massCategory;
        return dispatch(updateWorkItemPreset(p.id, updates));
      });
      await Promise.all(promises);
      setRowSelection({});
      setMassDealerships([]);
      setMassActive('');
      setMassPdfShow('');
      setMassCategory('');
      setTableKey(prev => prev + 1);
    } catch (err) {
      setError('Failed to mass assign');
    } finally {
      setUpdating(false);
    }
  };

  const handleMassAppend = async () => {
    const selected = Object.keys(rowSelection).map(i => data[parseInt(i)]);
    if (selected.length === 0) return;

    setUpdating(true);
    setError(null);

    try {
      const promises = selected.map(p => {
        const updates = {};
        if (massDealerships.length > 0) {
          const current = p.dealership?.split(',').filter(Boolean) || [];
          updates.dealership = [...new Set([...current, ...massDealerships])].join(',');
        }
        if (massActive !== '') updates.active = massActive === 'true';
        if (massPdfShow !== '') updates.dvi_pdf_show = massPdfShow === 'true';
        if (massCategory !== '') updates.category = massCategory;
        return dispatch(updateWorkItemPreset(p.id, updates));
      });
      await Promise.all(promises);
      setRowSelection({});
      setMassDealerships([]);
      setMassActive('');
      setMassPdfShow('');
      setMassCategory('');
      setTableKey(prev => prev + 1);
    } catch (err) {
      setError('Failed to append');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Box>
      {/* MASS ASSIGN BAR */}
      <Box sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={massCategory}
            onChange={(e) => setMassCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="">Select</MenuItem>
            {categoryOptions.map(c => (
              <MenuItem key={c} value={c}>
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Active</InputLabel>
          <Select
            value={massActive}
            onChange={(e) => setMassActive(e.target.value)}
            label="Active"
          >
            <MenuItem value="">Select</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>PDF Show</InputLabel>
          <Select
            value={massPdfShow}
            onChange={(e) => setMassPdfShow(e.target.value)}
            label="PDF Show"
          >
            <MenuItem value="">Select</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Dealerships</InputLabel>
          <Select
            multiple
            value={massDealerships}
            onChange={(e) => setMassDealerships(e.target.value)}
            label="Dealerships"
            renderValue={(selected) => selected.join(', ')}
          >
            {dealershipOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={massDealerships.includes(opt.value)} />
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleMassAssign}
          disabled={Object.keys(rowSelection).length === 0 || (massDealerships.length === 0 && massActive === '' && massPdfShow === '' && massCategory === '') || updating}
        >
          Override
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleMassAppend}
          disabled={Object.keys(rowSelection).length === 0 || (massDealerships.length === 0 && massActive === '' && massPdfShow === '' && massCategory === '') || updating}
        >
          Append
        </Button>
        {updating && <LinearProgress sx={{ flex: 1, ml: 2 }} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <MaterialReactTable
        key={tableKey}
        columns={columns}
        data={data}
        enableEditing
        editDisplayMode="cell"
        enableRowSelection
        enableMultiRowSelection
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        initialState={{
          density: 'compact',
          showColumnFilters: true,
        }}
        renderDetailPanel={({ row }) => (
          <DetailPanel preset={row.original} />
        )}
      />
    </Box>
  );
};

export default WorkItemPresetTable;